/* ═══ AgPanelHeader — Anagogia Design System ═══ */

import { X as CloseIcon } from 'lucide-react';
import { AgIconButton } from '../AgIconButton/AgIconButton';
import styles from './AgPanelHeader.module.css';

interface AgPanelHeaderProps {
  /** Icône d'identité du panneau, placée avant le titre */
  icon?: React.ReactNode;
  /** Libellé humain du panneau (jamais une clé technique) */
  title: string;
  /** Nombre d'éléments du panneau ; affiché en pastille quand > 0 */
  count?: number;
  /** Complément au compte, ex. « /12 » pour un total non filtré */
  countSuffix?: string;
  /** Actions propres au panneau, alignées à droite (AgIconButton en général) */
  actions?: React.ReactNode;
  /**
   * Fermeture du panneau. À ne fournir que lorsque le panneau porte lui-même sa
   * fermeture : docké dans une colonne à rail, c'est le rail qui ferme et la
   * croix ferait doublon.
   */
  onClose?: () => void;
  /** Libellé accessible du bouton de fermeture */
  closeLabel?: string;
  className?: string;
}

/**
 * Barre d'en-tête d'un panneau : identité à gauche, actions à droite.
 *
 * Une barre secondaire, un cran plus discrète que le chrome d'une modale :
 * fond `--ag-surface` sur une colonne `--ag-surface-2`, filet bas, aucun
 * dégradé. Elle donne aux panneaux d'une même colonne une seule géométrie.
 *
 * @category Navigation
 */
function AgPanelHeader({
  icon,
  title,
  count,
  countSuffix,
  actions,
  onClose,
  closeLabel = 'Fermer le panneau',
  className,
}: AgPanelHeaderProps) {
  const showCount = typeof count === 'number' && count > 0;

  return (
    <header className={[styles.header, className].filter(Boolean).join(' ')}>
      {icon && <span className={styles.icon}>{icon}</span>}
      <h2 className={styles.title}>{title}</h2>
      {showCount && (
        <span className={styles.count}>
          {count}
          {countSuffix}
        </span>
      )}
      {(actions || onClose) && (
        <div className={styles.actions}>
          {actions}
          {onClose && (
            <AgIconButton aria-label={closeLabel} size="sm" onClick={onClose}>
              <CloseIcon size={16} />
            </AgIconButton>
          )}
        </div>
      )}
    </header>
  );
}

export { AgPanelHeader };
export type { AgPanelHeaderProps };
export default AgPanelHeader;
